import React, { FC } from "react"
import { CSVError } from "../../../../../../../Helpers/Errors/CSVError"
import { SchemaValidationError } from "../../../../../../../Helpers/Errors/SchemaValidationError"
import { routePaths } from "../../../../../../../Routes"
import { ReactComponent as TableIcon } from "icons/lib/table.svg"
import { ReactComponent as BlankFileIcon } from "icons/lib/blank-file.svg"
import { ReactComponent as ErrIcon } from "icons/lib/bad-file.svg"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { IconInfo, Text } from "dash-styled"
import { OrderedMap } from "immutable"
import {
    JobSubmitDataParseError,
    JobSubmitDataParseStatus
} from "../../../../../../../Models/Client/JobSubmitDataParseStatus"
import { LinkButton } from "../../../../../LinkButton/LinkButton"
import { RemoveButton } from "../../../../../Button/RemoveButton"

interface ParseResultProps {
    fileType: "csv" | "json"
    parseState: JobSubmitDataParseStatus
    onClear: () => void
    onDownloadExample: () => void
}

function transformErrorArray(
    err: CSVError | SchemaValidationError,
    fileType: "csv" | "json",
    onDownloadExample: () => void
) {
    const downloadSample = (
        event: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => {
        event.preventDefault()
        event.stopPropagation()
        onDownloadExample()
    }
    if (err.name === "CSVError") {
        const error = err as CSVError
        const list: JSX.Element[] = []
        const missing = error.itemArray.filter(item => item.column === -1)
        const dupes = error.itemArray.filter(item => item.column !== -1)
        switch (missing.length) {
            case 0:
                break
            case 1:
                list.push(
                    <p
                        key={list.length}
                        className={`text-coral-700 text-sm ${
                            dupes.length ? "mb-2" : ""
                        }`}
                    >
                        Your file is missing a column called '{missing[0].value}
                        '. Sample file available{" "}
                        <LinkButton onClick={downloadSample}>here.</LinkButton>
                    </p>
                )
                break
            default:
                list.push(
                    <p
                        key={list.length}
                        className="text-coral-700 p-0 text-sm leading-4"
                    >
                        Your file is missing columns called:
                    </p>
                )
                missing.forEach(item => {
                    list.push(
                        <p
                            key={list.length}
                            className="text-coral-700 p-0 text-sm leading-4"
                        >
                            - '{item.value}'
                        </p>
                    )
                })
                list.push(
                    <p
                        key={list.length}
                        className={`text-coral-700 text-sm ${
                            dupes.length ? "mb-2" : ""
                        }`}
                    >
                        Sample file available{" "}
                        <LinkButton onClick={downloadSample}>here.</LinkButton>
                    </p>
                )
        }
        switch (dupes.length) {
            case 0:
                break
            case 1:
                list.push(
                    <p key={list.length} className="text-coral-700 text-sm">
                        Your file contains multiple columns for '
                        {dupes[0].value}
                        '. Please make sure there is only one.
                    </p>
                )
                break
            default:
                list.push(
                    <p
                        key={list.length}
                        className="text-coral-700 p-0 text-sm leading-4"
                    >
                        Your file contains duplicate columns for:
                    </p>
                )
                dupes.forEach(item => {
                    list.push(
                        <p
                            key={list.length}
                            className="text-coral-700 p-0 text-sm leading-4"
                        >
                            - '{item.value}'
                        </p>
                    )
                })
                list.push(
                    <p key={list.length} className="text-coral-700 text-sm">
                        Please make sure that there is only one of each.
                    </p>
                )
        }
        return list
    }

    type RowError =
        | { type: "too_big" }
        | {
              type: "other"
              missingColumns: string[]
              invalidColumns: { name: string; value: string | undefined }[]
          }

    const initialOtherError: RowError = {
        type: "other",
        missingColumns: [],
        invalidColumns: []
    }
    const error = err as SchemaValidationError
    const errorsByRow: OrderedMap<number, RowError> = error.itemArray
        .reduce((passed, { type, row, column, value }) => {
            switch (type) {
                case "missing":
                    return passed.update(row, initialOtherError, i => {
                        return i.type === "too_big"
                            ? i
                            : {
                                  ...i,
                                  missingColumns: i.missingColumns.concat([
                                      column
                                  ])
                              }
                    })
                case "other":
                    return passed.update(row, initialOtherError, i => {
                        return i.type === "too_big"
                            ? i
                            : {
                                  ...i,
                                  invalidColumns: i.invalidColumns.concat([
                                      { name: column, value: value }
                                  ])
                              }
                    })
                case "too_big":
                    return passed.set(row, { type: "too_big" })
                default:
                    return passed
            }
        }, OrderedMap<number, RowError>())
        .sortBy((_, rowNumber) => rowNumber)

    return [
        <p key="summary" className="text-coral-700 text-sm mb-2">
            We found problems with{" "}
            {errorsByRow.count() === error.totalRows
                ? "all"
                : `${
                      error.trimmed ? "more than " : ""
                  } ${errorsByRow.count()} of ${error.totalRows}`}{" "}
            {fileType === "csv" ? "rows" : "items"}:
        </p>
    ].concat(
        errorsByRow.toArray().flatMap<JSX.Element>(([rowIndex, row]) => {
            const cls = "text-coral-700 p-0 text-sm leading-4"
            const heading =
                fileType === "csv"
                    ? `Row ${rowIndex + 2}: `
                    : `Item at index ${rowIndex}: `

            switch (row.type) {
                case "too_big":
                    return [
                        <p key={rowIndex} className={cls}>
                            {heading}Size exceeds 250KB
                        </p>
                    ]
                case "other":
                    const missing = row.missingColumns
                    const invalid = row.invalidColumns

                    return (
                        missing.length > 0
                            ? [
                                  <p key={rowIndex} className={cls}>
                                      {heading}Missing value
                                      {missing.length > 1 ? "s" : ""} for{" "}
                                      {missing
                                          .map(col => `'${col}'`)
                                          .join(", ")}
                                  </p>
                              ]
                            : []
                    ).concat(
                        invalid.map(({ name, value }, colIndex) => (
                            <p key={`${rowIndex}_${colIndex}`} className={cls}>
                                {heading}Invalid value for '{name}'
                                {value ? ` ('${value}')` : ""}
                            </p>
                        ))
                    )
                default:
                    return []
            }
        })
    )
}

interface ParseErrRenderProps {
    error: JobSubmitDataParseError
    fileType: "csv" | "json"
    onDownloadExample: () => void
}

export const ParseErrRender: FC<ParseErrRenderProps> = ({
    error,
    fileType,
    onDownloadExample
}) => {
    const [allErrorsShown, showAllErrors] = useBoolean(false)

    switch (error.type) {
        case "format":
            return (
                <p className="text-coral-700 text-sm">
                    This file isn't in the required{" "}
                    {fileType === "csv" ? "CSV" : "JSON"} format.{" "}
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-pink-500"
                        href={routePaths.docs("how-to-format-csv")}
                    >
                        More info here.
                    </a>
                </p>
            )
        case "too_big":
            return (
                <p className="text-coral-700 text-sm">
                    This file is too big - please make sure it's not over 50MB.
                    (You can always upload multiple files)
                </p>
            )
        case "other":
            return <p className="text-coral-700 text-sm">{error.message}</p>
        case "validation":
            const children = transformErrorArray(
                error.error,
                fileType,
                onDownloadExample
            )

            return (
                <>
                    {allErrorsShown || children.length < 6
                        ? children
                        : children.slice(0, 5)}
                    {!allErrorsShown && children.length > 5 && (
                        <LinkButton onClick={showAllErrors} className="mt-3">
                            Show {children.length - 5} more
                        </LinkButton>
                    )}
                </>
            )
    }
}

export const ParseResult: FC<ParseResultProps> = ({
    parseState,
    fileType,
    onClear,
    onDownloadExample
}) => {
    const info = parseState.type === "parsed" ? parseState.messages : []
    const trimmed = parseState.type === "parsed" && parseState.data.isTrimmed
    const hasError = parseState.type === "failed"
    const fileName =
        parseState.type === "idle" ? undefined : parseState.fileName
    const iconCls = "w-8 h-8 text-rhino-800"

    return (
        <>
            {parseState.type === "failed" && (
                <div className="bg-coral-200 p-4 rounded overflow-x-auto my-4">
                    <ParseErrRender
                        error={parseState.error}
                        fileType={fileType}
                        onDownloadExample={onDownloadExample}
                    />
                </div>
            )}
            {fileName && (
                <div
                    className={`flex justify-between items-center ${
                        hasError ? "my-4" : "my-6"
                    }`}
                >
                    {hasError ? (
                        <ErrIcon className={iconCls} />
                    ) : fileType === "csv" ? (
                        <TableIcon className={iconCls} />
                    ) : (
                        <BlankFileIcon className={iconCls} />
                    )}
                    <p
                        className="ml-4 flex-grow"
                        data-testid="file-name-parsed"
                    >
                        {fileName}
                    </p>
                    <RemoveButton icon="close" onRemove={onClear} />
                </div>
            )}
            {fileName &&
                info.length > 0 &&
                info.map((item, index) => (
                    <div
                        key={`${index}_info`}
                        className={`flex items-center bg-rhino-250 rounded py-4 pr-4 pl-3 text-smoke-350 ${
                            index !== info.length - 1 || trimmed
                                ? "mb-4"
                                : "mb-6"
                        }`}
                    >
                        <IconInfo />
                        <Text className="text-rhino-800 pl-2 text-sm">
                            {item}
                        </Text>
                    </div>
                ))}
        </>
    )
}
